// Agents (firm-wide): every engine's heartbeat, the agent runs that are live across engagements, the run history, and the role
// cards the runs used (with their tool and guidance versions). Agents are delegates: each run works for a named person, who stays
// owner of record. Live runs carry their controls (pause, resume, steer, take over, stop), open or closed from the run's published
// controls with the engine's reason; each is one command through the inbox for that run's engagement.
import { useState } from "react";
import { Link } from "react-router-dom";
import { ActionDrawer, type ActionIntent } from "../app/ActionDrawer";
import { ONLINE_SECONDS, STALE_SECONDS } from "../app/EngineStatus";
import { useApp } from "../app/context";
import { AgentChip, Card, DataTable, PageHeader, StatusPill } from "../components";
import type { AgentRun } from "../data/types";
import { ago, fmtStamp, plural } from "../lib/format";
import { useLoad, useRealtime } from "../lib/hooks";
import { RunControlButtons, RunControlState } from "./agentControls";

export function AgentsPage() {
  const { ds } = useApp();
  const beats = useLoad(() => ds.heartbeat(), [ds]);
  const runs = useLoad(async () => {
    const es = await ds.engagements();
    const per = await Promise.all(es.map(async (e) => (await ds.table(e.id, "agent_runs").catch(() => [] as AgentRun[])).map((r) => ({ ...r, client: e.clientName }))));
    return per.flat().sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  }, [ds]);
  useRealtime(ds, "heartbeat", beats.reload);
  useRealtime(ds, "actions", runs.reload);
  const [intent, setIntent] = useState<ActionIntent | null>(null);
  const openFor = (r: AgentRun) => (command: string, title: string, fixed?: Record<string, unknown>, initial?: Record<string, unknown>) =>
    setIntent({ command, engagementId: r.engagementId, fixed, initial, title });
  const live = (runs.data ?? []).filter((r) => !r.endedAt);
  const cards = new Map<string, { runs: number; live: number; tools: Set<string>; guidance: Set<string>; people: Set<string>; last: string }>();
  for (const r of runs.data ?? []) {
    const c = cards.get(r.roleCard) ?? { runs: 0, live: 0, tools: new Set(), guidance: new Set(), people: new Set(), last: r.startedAt };
    c.runs++; if (!r.endedAt) c.live++;
    if (r.toolVersion) c.tools.add(r.toolVersion); if (r.guidanceVersion) c.guidance.add(r.guidanceVersion); if (r.person) c.people.add(r.person);
    if (r.startedAt > c.last) c.last = r.startedAt;
    cards.set(r.roleCard, c);
  }
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader title="Agents" subtitle="Engines, live agent runs across engagements, run history and role cards. Each run works for a named person, who stays owner of record." />
      <Card title="Engines" bodyClass="p-3">
        {beats.error ? <p role="alert" className="text-sm text-blocked">{beats.error}</p> : !beats.data ? <p className="text-sm text-muted">Loading…</p> : beats.data.length === 0 ? <p className="text-sm text-muted">No engine has published a heartbeat.</p> : (
          <ul className="divide-y divide-line text-sm">
            {beats.data.map((b) => {
              const age = (Date.now() - new Date(b.lastSeen).getTime()) / 1000;
              const st = ds.mode === "demo" ? ["info", "Demo snapshot"] : age <= ONLINE_SECONDS ? ["done", "Online"] : age <= STALE_SECONDS ? ["waiting", "Slow"] : ["blocked", "Offline"];
              return (
                <li key={b.engineId} className="flex flex-wrap items-center gap-2 py-2">
                  <StatusPill state={st[0]} label={st[1]} /><span className="font-medium">{b.holderName}'s engine</span><span className="font-mono text-xs text-muted">{b.engineId} · {b.buildVersion}</span>
                  <span className="basis-full text-xs text-muted">last beat {fmtStamp(b.lastSeen)} ({ago(b.lastSeen)}) · push {fmtStamp(b.lastPush)} · pull {fmtStamp(b.lastPull)} · {plural(b.inboxPending, "request")} waiting · serves {plural(b.engagementsServed.length, "engagement")}</span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      <Card title={`Live runs (${live.length})`} bodyClass="p-3">
        <RunTable rows={runs.data ? live : undefined} loading={runs.loading} error={runs.error} onRetry={runs.reload} empty="No agent is running now." controls={openFor} />
      </Card>
      <Card title="Role cards in use" bodyClass="p-3">
        {cards.size === 0 ? <p className="text-sm text-muted">No run recorded yet on your engagements.</p> : (
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {[...cards.entries()].map(([name, c]) => (
              <li key={name} className="rounded-md border border-agent-line bg-agent-bg p-3 text-sm">
                <AgentChip name={name} />
                <p className="mt-1">{plural(c.runs, "run")} · {c.live} live · last {fmtStamp(c.last)}</p>
                <p className="text-xs text-muted">for {[...c.people].join(", ") || "—"}</p>
                <p className="text-xs text-muted">tool {[...c.tools].join(", ") || "—"} · guidance {[...c.guidance].join(", ") || "—"}</p>
                <p className="mt-1 font-mono text-[11px] text-muted">agents/roles/{name}.md</p>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Run history" bodyClass="p-3">
        <RunTable rows={runs.data} loading={runs.loading} error={runs.error} onRetry={runs.reload} empty="No run recorded yet." />
      </Card>
      <ActionDrawer intent={intent} onClose={() => setIntent(null)} onSent={runs.reload} />
    </div>
  );
}

function RunTable({ rows, loading, error, onRetry, empty, controls }: {
  rows: Array<AgentRun & { client: string }> | undefined; loading: boolean; error?: string; onRetry: () => void; empty: string;
  controls?: (r: AgentRun) => (command: string, title: string, fixed?: Record<string, unknown>, initial?: Record<string, unknown>) => void;
}) {
  return (
    <DataTable rows={rows} loading={loading} error={error} onRetry={onRetry} rowKey={(r) => `${r.engagementId}:${r.id}`} caption="Agent runs" dense empty={empty}
      filter={{ id: "state", label: "state", value: (r) => r.state }}
      columns={[
        { id: "role", header: "Role", cell: (r) => <AgentChip name={r.roleCard} /> },
        { id: "task", header: "Task", cell: (r) => <span>{r.task ?? "—"}<span className="block text-xs text-muted">last: {r.lastCommand ?? "—"}{r.refusals.length ? ` · ${plural(r.refusals.length, "refusal")}` : ""}</span></span> },
        { id: "eng", header: "Engagement", cell: (r) => <Link className="hover:underline" to={`/e/${r.engagementId}/details/agents`}>{r.client}</Link>, sortValue: (r) => r.client },
        { id: "for", header: "For", cell: (r) => r.person ?? "—" },
        { id: "state", header: "State", cell: (r) => <span className="inline-flex flex-col gap-1"><StatusPill state={r.state === "active" ? "running" : r.state} live={r.state === "active"} /><RunControlState r={r} /></span>, sortValue: (r) => r.state },
        { id: "started", header: "Started", cell: (r) => ago(r.startedAt), sortValue: (r) => r.startedAt },
        ...(controls ? [{ id: "controls", header: "Controls", cell: (r: AgentRun & { client: string }) => <RunControlButtons r={r} open={controls(r)} /> }] : []),
      ]} />
  );
}
